import de_DE from '@uppy/locales/lib/de_DE';
import en_US from '@uppy/locales/lib/en_US';

export type UppyLocale = typeof de_DE;

/**
 * "Du"-form overrides for the German Uppy locale pack.
 * The upstream `de_DE` pack uses the formal "Sie" form, but our app
 * consistently uses the informal "Du" form.
 */
const deOverrides: UppyLocale['strings'] = {
  exceedsSize:
    '%{file} ist größer als die maximal erlaubte Dateigröße von %{size}. Wähle eine kleinere Datei aus.',
  inferiorSize:
    'Diese Datei ist kleiner als die erforderliche Mindestgröße von %{size}. Wähle eine größere Datei aus.',
  noDuplicates:
    "'%{fileName}' ist bereits vorhanden. Wähle eine andere Datei aus.",
  youCanOnlyUploadFileTypes:
    'Du kannst nur %{types}-Dateien hochladen. Wähle eine passende Datei aus.',
  youCanOnlyUploadX: {
    '0': 'Du kannst nur eine Datei hochladen. Entferne eine Datei, bevor du eine neue hinzufügst.',
    '1': 'Du kannst nur %{smart_count} Dateien hochladen. Wähle weniger Dateien aus.',
  },
  youHaveToAtLeastSelectX: {
    '0': 'Du musst mindestens eine Datei auswählen.',
    '1': 'Du musst mindestens %{smart_count} Dateien auswählen. Füge weitere Dateien hinzu.',
  },
};

const packs: Record<string, UppyLocale> = {
  de: de_DE,
  en: en_US,
};

const enOverrides: UppyLocale['strings'] = {
  exceedsSize:
    '%{file} exceeds the maximum allowed size of %{size}. Please choose a smaller file.',
  inferiorSize:
    'This file is smaller than the required minimum size of %{size}. Please choose a larger file.',
  noDuplicates:
    "The file '%{fileName}' already exists. Please choose a different file.",
  youCanOnlyUploadFileTypes:
    'You can only upload the following file types: %{types}. Please choose a compatible file.',
  youCanOnlyUploadX: {
    '0': 'You can only upload one file. Remove a file before adding a new one.',
    '1': 'You can only upload %{smart_count} files. Please select fewer files.',
  },
  youHaveToAtLeastSelectX: {
    '0': 'You must select at least one file.',
    '1': 'You must select at least %{smart_count} files. Please add more files.',
  },
};

const componentOverrides: Record<string, UppyLocale['strings']> = {
  de: deOverrides,
  en: enOverrides,
};

/**
 * Returns a complete Uppy locale object for the given app locale.
 *
 * Merge order (later wins):
 *   1. Upstream Uppy locale pack (de_DE / en_US)
 *   2. Component-level overrides (e.g. Sie → Du for German)
 *   3. Per-instance overrides passed by the caller
 */
export function getUppyLocale(
  appLocale: string,
  instanceOverrides?: Partial<UppyLocale['strings']>,
): UppyLocale {
  const base = packs[appLocale] ?? en_US;
  const compOverrides = componentOverrides[appLocale];

  return {
    ...base,
    strings: {
      ...base.strings,
      ...compOverrides,
      ...instanceOverrides,
    },
  };
}
